import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Map, useMap } from '@vis.gl/react-google-maps'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { DeckProps } from '@deck.gl/core'
import { IconLayer } from '@deck.gl/layers'
import { MilesByStateData } from 'interfaces/ISummaryReport'
import { svgToDataURL, DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from 'tools/mapHelper'
import {
  getIconSize,
  getStateIconSvg,
  getTooltipData,
} from './milesByStateMapUtils'

interface IMilesByStateMapProps {
  data: MilesByStateData[]
}

const DeckGLOverlay = (props: DeckProps) => {
  const map = useMap()
  const overlay = useMemo(
    () =>
      new GoogleMapsOverlay({
        ...props,
        getCursor: ({ isHovering, isDragging }) => {
          if (map) {
            if (isHovering) {
              map.setOptions({ draggableCursor: 'pointer' })
            } else {
              map.setOptions({ draggableCursor: '' })
            }
          }
          return isDragging ? 'grabbing' : 'grab'
        },
        getTooltip: ({ object, layer }) => {
          return getTooltipData(object, layer)
        },
      }),
    [map]
  )

  useEffect(() => {
    if (map) {
      overlay.setMap(map)
      return () => {
        overlay.setMap(null)
        map.setOptions({ draggableCursor: '' })
      }
    }
  }, [map, overlay])

  useEffect(() => {
    overlay.setProps(props)
  }, [overlay, props])

  return null
}

const MilesByStateMap: React.FC<IMilesByStateMapProps> = ({ data }) => {
  const map = useMap()
  const [defaultZoom] = useState(DEFAULT_MAP_ZOOM)
  const [defaultCenter] = useState(DEFAULT_MAP_CENTER)
  const [currentZoom, setCurrentZoom] = useState(DEFAULT_MAP_ZOOM)

  const milesByStateData = useMemo(() => {
    if (!data || data.length === 0) return []

    return data
      .filter(
        (item) =>
          item.centralPoint &&
          item.centralPoint.latitude &&
          item.centralPoint.longitude
      )
      .map((item, index) => ({
        position: [item.centralPoint.longitude, item.centralPoint.latitude],
        id: item.state.code,
        state: item.state.name,
        miles: item.metrics.distanceMiles,
        percentageOfTotal: item.metrics.percentageOfTotal,
      }))
  }, [data])

  const geoJsonPoints = useMemo(() => {
    if (!milesByStateData || milesByStateData.length === 0) return []

    return milesByStateData.map((item) => ({
      type: 'Feature' as const,
      properties: {
        id: item.id,
        state: item.state,
        miles: item.miles,
        percentageOfTotal: item.percentageOfTotal,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: item.position,
      },
    }))
  }, [milesByStateData])

  const layers = useMemo(() => {
    if (!geoJsonPoints || geoJsonPoints.length === 0) return []
    return [
      new IconLayer({
        id: 'miles-by-state-icon-layer',
        data: geoJsonPoints,
        getIcon: (d) => {
          const iconSize = 200
          return {
            url: svgToDataURL(getStateIconSvg(d.properties.id)),
            width: iconSize,
            height: iconSize,
          }
        },
        getPosition: (d) => d.geometry.coordinates,
        getSize: (d) => getIconSize(d.properties.percentageOfTotal),
        pickable: true,
      }),
    ]
  }, [geoJsonPoints])

  return (
    <div style={{ height: '350px', width: '100%' }}>
      <Map
        defaultZoom={defaultZoom}
        defaultCenter={defaultCenter}
        gestureHandling={'greedy'}
        disableDefaultUI={true}
        clickableIcons={false}
        mapId={'MILES_BY_STATE_MAP'}
      >
        <DeckGLOverlay layers={layers} />
      </Map>
    </div>
  )
}

export default MilesByStateMap